// src/ContactUsData.js
import React, { useState, useEffect, useCallback } from 'react';
import { textdb } from '../../firebase'; // Adjust path if needed
import { collection, getDocs, doc, deleteDoc, writeBatch, query, orderBy } from "firebase/firestore";
import * as XLSX from 'xlsx'; // For Excel export
import { color } from 'framer-motion';

const adminStyles = {
  container: {
    padding: '20px',
    fontFamily: 'Arial, sans-serif',
    maxWidth: '1200px',
    margin: '20px auto',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    borderBottom: '1px solid #eee',
    paddingBottom: '10px',
  },
  title: { color: '#333', fontSize: '24px' },
  button: {
    padding: '10px 15px',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '14px',
    marginLeft: '10px',
  },
  exportButton: { backgroundColor: '#28a745', color: 'white' },
  deleteAllButton: { backgroundColor: '#dc3545', color: 'white' },
  refreshButton: { backgroundColor: '#007bff', color: 'white' },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: '20px',
  },
  th: {
    border: '1px solid #ddd',
    padding: '12px',
    textAlign: 'left',
    backgroundColor: '#01062c', // Main color from theme
    color: '#64ffda', // Secondary color from theme
    fontWeight: 'bold',
  },
  td: {
    border: '1px solid #ddd',
    padding: '10px',
    verticalAlign: 'top',
    color:"black"
  },
  deleteRowButton: {
    padding: '6px 10px',
    backgroundColor: '#ffc107',
    color: 'black',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '13px',
  },
  messageCell: {
    whiteSpace: 'pre-wrap', // To respect newlines in message
    wortextdbreak: 'break-word',
  },
  loading: { textAlign: 'center', padding: '20px', fontSize: '18px', color: '#555' },
  error: { textAlign: 'center', padding: '20px', fontSize: '18px', color: 'red' },
  noData: { textAlign: 'center', padding: '20px', fontSize: '18px', color: '#777' },
};

const ContactUsData = () => {
  const [submissions, setSubmissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchSubmissions = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const q = query(collection(textdb, "contactSubmissions"), orderBy("submittedAt", "desc"));
      const querySnapshot = await getDocs(q);
      const subs = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        // Convert Firestore Timestamp to readable date string
        submittedAt: doc.data().submittedAt?.toDate().toLocaleString() || 'N/A'
      }));
      setSubmissions(subs);
    } catch (err) {
      console.error("Error fetching submissions: ", err);
      setError("Failed to load submissions. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const handleDeleteOne = async (id) => {
    if (window.confirm("Are you sure you want to delete this submission?")) {
      try {
        await deleteDoc(doc(textdb, "contactSubmissions", id));
        setSubmissions(prev => prev.filter(sub => sub.id !== id));
      } catch (err) {
        console.error("Error deleting document: ", err);
        alert("Failed to delete submission.");
      }
    }
  };

  const handleDeleteAll = async () => {
    if (window.confirm("ARE YOU SURE you want to delete ALL submissions? This action cannot be undone.")) {
      setIsLoading(true);
      try {
        const querySnapshot = await getDocs(collection(textdb, "contactSubmissions"));
        if (querySnapshot.empty) {
            alert("No submissions to delete.");
            setIsLoading(false);
            return;
        }
        const batch = writeBatch(textdb);
        querySnapshot.docs.forEach(doc => {
          batch.delete(doc.ref);
        });
        await batch.commit();
        setSubmissions([]);
        alert("All submissions deleted successfully.");
      } catch (err) {
        console.error("Error deleting all documents: ", err);
        alert("Failed to delete all submissions.");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleExportToExcel = () => {
    if (submissions.length === 0) {
        alert("No data to export.");
        return;
    }
    // Prepare data for Excel (remove id or reorder columns as needed)
    const dataToExport = submissions.map(({ id, ...rest }) => rest);

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "ContactSubmissions");

    // Trigger download
    XLSX.writeFile(workbook, "ContactSubmissions.xlsx");
  };

  if (isLoading) return <div style={adminStyles.loading}>Loading submissions...</div>;
  if (error) return <div style={adminStyles.error}>{error}</div>;

  return (
    <div style={adminStyles.container}>
      <div style={adminStyles.header}>
        <h1 style={adminStyles.title}>Contact Form Submissions</h1>
        <div>
          <button onClick={fetchSubmissions} style={{...adminStyles.button, ...adminStyles.refreshButton}}>Refresh</button>
          <button onClick={handleExportToExcel} style={{...adminStyles.button, ...adminStyles.exportButton}}>Export to Excel</button>
          <button onClick={handleDeleteAll} style={{...adminStyles.button, ...adminStyles.deleteAllButton}}>Delete All</button>
        </div>
      </div>

      {submissions.length === 0 ? (
        <div style={adminStyles.noData}>No submissions yet.</div>
      ) : (
        <div style={{overflowX: 'auto'}}>
            <table style={adminStyles.table}>
            <thead>
                <tr>
                <th style={adminStyles.th}>Submitted At</th>
                <th style={adminStyles.th}>Name</th>
                <th style={adminStyles.th}>Email</th>
                <th style={adminStyles.th}>Phone</th>
                <th style={adminStyles.th}>Subject</th>
                <th style={adminStyles.th}>Message</th>
                <th style={adminStyles.th}>Actions</th>
                </tr>
            </thead>
            <tbody>
                {submissions.map(sub => (
                <tr key={sub.id}>
                    <td style={adminStyles.td}>{sub.submittedAt}</td>
                    <td style={adminStyles.td}>{sub.name}</td>
                    <td style={adminStyles.td}>{sub.email}</td>
                    <td style={adminStyles.td}>{sub.phone || '-'}</td>
                    <td style={adminStyles.td}>{sub.subject}</td>
                    <td style={{...adminStyles.td, ...adminStyles.messageCell}}>{sub.message}</td>
                    <td style={adminStyles.td}>
                    <button onClick={() => handleDeleteOne(sub.id)} style={adminStyles.deleteRowButton}>Delete</button>
                    </td>
                </tr>
                ))}
            </tbody>
            </table>
        </div>
      )}
    </div>
  );
};

export default ContactUsData;